import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BasicCli from '../../../main/components/basicCli/BasicCli';
import axiosInstance from '../../../main/initializers/axios';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import IUser from '../../../main/interfaces/IUser';
import useGetUser from '../../../main/hooks/useGetUser';
import './RoomHandoverPage.css';

type HotelPaymentDto = {
  id: number;
  hotelFolioId?: number | null;
  reservationId?: number | null;
  roomNumber?: string | null;
  guestName?: string | null;
  amount: number;
  method?: number | null;
  paidAt?: string | null;
  postedByUserId?: number | null;
  closed?: boolean | null;
  folioStatus?: number | null;
  folioTotalAmount?: number | null;
  folioOpenAt?: string | null;
  folioCloseAt?: string | null;
  finalInvoiceId?: number | null;
};

type PaymentHistoryVm = {
  id: number;
  folioId: number;
  reservationId: number;
  roomName: string;
  guestName: string;
  amount: number;
  paidAt: string | null;
  method: number | null;
  folioStatus: number | null;
  folioTotal: number;
  folioOpenedAt: string | null;
  folioClosedAt: string | null;
  closed: boolean;
  invoiceId?: number | null;
};

const todayForInput = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const firstDayOfMonthForInput = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
};

const resolveGuestName = (item: any) => {
  const directName = item?.guestName ?? item?.GuestName ?? item?.primaryGuestName ?? item?.PrimaryGuestName;
  if (typeof directName === 'string' && directName.trim()) return directName.trim();

  const nestedGuest = item?.guest ?? item?.Guest ?? item?.primaryGuest ?? item?.PrimaryGuest;
  const firstName = nestedGuest?.firstName ?? nestedGuest?.FirstName ?? '';
  const lastName = nestedGuest?.lastName ?? nestedGuest?.LastName ?? '';
  const fullName = `${firstName} ${lastName}`.trim();
  if (fullName) return fullName;

  return '';
};

const resolveRoomName = (item: any, fallbackId: number, fallbackRoomLabel: string) =>
  item?.roomNumber ??
  item?.RoomNumber ??
  item?.room?.roomNumber ??
  item?.Room?.RoomNumber ??
  `${fallbackRoomLabel} #${item?.roomId ?? item?.RoomId ?? fallbackId}`;

const isWithinRange = (value: string | null | undefined, from: string, to: string) => {
  if (!value) return false;
  const datePart = value.split('T')[0];
  return datePart >= from && datePart <= to;
};

const RoomHandoverPage: React.FC = () => {
  const { t } = useTranslation();
  const currentUser = useGetUser();

  const [users, setUsers] = useState<IUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [dateFrom, setDateFrom] = useState(firstDayOfMonthForInput());
  const [dateTo, setDateTo] = useState(todayForInput());
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistoryVm[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [closingDaily, setClosingDaily] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const currencySuffix = currentUser?.companyCurrencySymbol || 'ALL';
  const fallbackRoomLabel = t('roomHandover.fallbackRoomName', { defaultValue: 'Room' });
  const shouldFilterByUser = Number(selectedUserId) > 0;

  const formatMoney = useCallback(
    (value: number) =>
      `${new Intl.NumberFormat('sq-AL', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(value) || 0)} ${currencySuffix}`,
    [currencySuffix],
  );

  const formatDate = useCallback((value?: string | null) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString('sq-AL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  const getPaymentMethodLabel = useCallback(
    (method?: number | null) => {
      if (method === 1) return t('hotelBilling.paymentMethods.cash', { defaultValue: 'Cash' });
      if (method === 2) return t('hotelBilling.paymentMethods.card', { defaultValue: 'Card' });
      if (method === 3) return t('hotelBilling.paymentMethods.bank', { defaultValue: 'Bank' });
      if (method === 4) return t('hotelBilling.paymentMethods.online', { defaultValue: 'Online' });
      return '-';
    },
    [t],
  );

  const fetchUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);
      const response = await axiosInstance.get(ApiConstants.User.users);
      const activeUsers: IUser[] = (response.data ?? []).filter((item: IUser) => !item.isDelete);
      setUsers(activeUsers);
    } catch (err) {
      console.error(err);
      setError(t('roomHandover.loadUsersError', { defaultValue: 'Failed to load users.' }));
    } finally {
      setLoadingUsers(false);
    }
  }, [t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const fetchPaymentHistory = useCallback(async () => {
    const parsedUserId = Number(selectedUserId);

    try {
      setLoadingData(true);
      setError('');
      setHasSearched(true);

      const response = await axiosInstance.get<HotelPaymentDto[]>(ApiConstants.hotelFolio.paymentHistory, {
        params: {
          startDate: dateFrom,
          endDate: dateTo,
          userId: shouldFilterByUser ? parsedUserId : undefined,
        },
      });

      const payments = Array.isArray(response.data) ? response.data : [];
      setPaymentHistory(
        payments.map((payment) => ({
          id: Number(payment.id ?? 0),
          folioId: Number(payment.hotelFolioId ?? 0),
          reservationId: Number(payment.reservationId ?? 0),
          roomName: payment.roomNumber?.trim() || `${fallbackRoomLabel} #${payment.hotelFolioId ?? payment.id ?? 0}`,
          guestName: payment.guestName?.trim() || '',
          amount: Number(payment.amount ?? 0),
          paidAt: payment.paidAt ?? null,
          method: typeof payment.method === 'number' ? payment.method : null,
          folioStatus: typeof payment.folioStatus === 'number' ? payment.folioStatus : null,
          folioTotal: Number(payment.folioTotalAmount ?? 0),
          folioOpenedAt: payment.folioOpenAt ?? null,
          folioClosedAt: payment.folioCloseAt ?? null,
          closed: Boolean(payment.closed),
          invoiceId: payment.finalInvoiceId ?? null,
        })),
      );
    } catch (err) {
      console.error(err);
      setPaymentHistory([]);
      setError(t('roomHandover.loadDataError', { defaultValue: 'Failed to load room data for this user.' }));
    } finally {
      setLoadingData(false);
    }
  }, [dateFrom, dateTo, fallbackRoomLabel, selectedUserId, shouldFilterByUser, t]);

  const handleClear = () => {
    setSelectedUserId('');
    setDateFrom(firstDayOfMonthForInput());
    setDateTo(todayForInput());
    setPaymentHistory([]);
    setError('');
    setHasSearched(false);
  };

  const handleCloseHotelDaily = useCallback(async () => {
    const parsedUserId = Number(selectedUserId);
    if (!parsedUserId || parsedUserId <= 0) {
      setError(t('roomHandover.requiredUser', { defaultValue: 'Please select a valid user.' }));
      return;
    }

    try {
      setClosingDaily(true);
      setError('');
      await axiosInstance.post(
        `${ApiConstants.hotelFolio.closeOpenedByUser}?userId=${parsedUserId}&startDate=${dateFrom}&endDate=${dateTo}`,
      );
      await fetchPaymentHistory();
    } catch (err) {
      console.error(err);
      setError(t('roomHandover.closeDailyError', { defaultValue: 'Failed to close hotel folios for the selected user.' }));
    } finally {
      setClosingDaily(false);
    }
  }, [dateFrom, dateTo, fetchPaymentHistory, selectedUserId, t]);

  const selectedUser = useMemo(() => users.find((item) => Number(item.id) === Number(selectedUserId)) ?? null, [users, selectedUserId]);
  const canAutoSearch = Boolean(dateFrom && dateTo && selectedUserId);

  useEffect(() => {
    if (!canAutoSearch) {
      setPaymentHistory([]);
      setError('');
      setHasSearched(false);
      return;
    }

    if (dateFrom > dateTo) {
      setPaymentHistory([]);
      setHasSearched(false);
      setError(t('roomHandover.invalidDateRange', { defaultValue: 'Date from cannot be later than date to.' }));
      return;
    }

    fetchPaymentHistory();
  }, [canAutoSearch, dateFrom, dateTo, fetchPaymentHistory, selectedUserId, t]);

  const handoverAmount = useMemo(() => paymentHistory.filter((item) => !item.closed).reduce((sum, item) => sum + item.amount, 0), [paymentHistory]);
  const uniqueFolios = useMemo(() => Array.from(new Set(paymentHistory.map((item) => item.folioId))), [paymentHistory]);
  const openFolios = useMemo(
    () => Array.from(new Set(paymentHistory.filter((item) => item.folioStatus === 1).map((item) => item.folioId))),
    [paymentHistory],
  );
  const closedFolios = useMemo(
    () => Array.from(new Set(paymentHistory.filter((item) => item.folioStatus === 2).map((item) => item.folioId))),
    [paymentHistory],
  );

  const renderPaymentHistory = () => {
    if (paymentHistory.length === 0) {
      return <div className="rhEmpty">{t('roomHandover.emptyPayments', { defaultValue: 'No payment history found for the selected filters.' })}</div>;
    }

    return (
      <div className="rhTableWrap">
        <table className="rhTable">
          <thead>
            <tr>
              <th>{t('roomHandover.labels.room', { defaultValue: 'Room' })}</th>
              <th>{t('roomHandover.labels.folioId', { defaultValue: 'Folio' })}</th>
              <th>{t('roomHandover.labels.invoiceId', { defaultValue: 'Invoice' })}</th>
              <th>{t('roomHandover.labels.guest', { defaultValue: 'Guest' })}</th>
              <th>{t('roomHandover.labels.paymentMethod', { defaultValue: 'Method' })}</th>
              <th>{t('roomHandover.labels.delivery', { defaultValue: 'Amount' })}</th>
              <th>{t('roomHandover.labels.total', { defaultValue: 'Folio total' })}</th>
              <th>{t('roomHandover.labels.paymentDate', { defaultValue: 'Payment date' })}</th>
              <th>{t('roomHandover.labels.closedAt', { defaultValue: 'Closed at' })}</th>
              <th>{t('roomHandover.labels.closed', { defaultValue: 'Closed' })}</th>
              <th>{t('generic.status', { defaultValue: 'Status' })}</th>
            </tr>
          </thead>
          <tbody>
            {paymentHistory.map((item) => (
              <tr key={`${item.folioId}-${item.id}`}>
                <td>{item.roomName}</td>
                <td>#{item.folioId}</td>
                <td>{item.invoiceId ? `#${item.invoiceId}` : '-'}</td>
                <td>{item.guestName || '-'}</td>
                <td>{getPaymentMethodLabel(item.method)}</td>
                <td>{formatMoney(item.amount)}</td>
                <td>{formatMoney(item.folioTotal)}</td>
                <td>{formatDate(item.paidAt)}</td>
                <td>{formatDate(item.folioClosedAt)}</td>
                <td>
                  <span className={`rhClosedFlag ${item.closed ? 'isTrue' : 'isFalse'}`}>
                    {item.closed ? '✓✓' : 'false'}
                  </span>
                </td>
                <td>
                  <span className={`rhStatus ${item.folioStatus === 2 ? 'isClosed' : 'isOpen'}`}>
                    {item.folioStatus === 2
                      ? t('roomHandover.status.closed', { defaultValue: 'Closed' })
                      : t('roomHandover.status.open', { defaultValue: 'Open' })}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <BasicCli>
      <div className="rhPage bcPageContent">
        <div className="rhHeader">
          <div>
            <h1 className="rhTitle">{t('roomHandover.title', { defaultValue: 'Room Handover' })}</h1>
            <p className="rhSubtitle">
              {t('roomHandover.subtitle', {
                defaultValue: 'Filter by date range and selected user to see hotel folio payment history and the amount to hand over to the manager.',
              })}
            </p>
          </div>
        </div>

        <div className="rhFilters">
          <div className="rhField">
            <label htmlFor="rh-date-from">{t('roomHandover.filters.dateFrom', { defaultValue: 'Date from' })}</label>
            <input id="rh-date-from" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
          </div>

          <div className="rhField">
            <label htmlFor="rh-date-to">{t('roomHandover.filters.dateTo', { defaultValue: 'Date to' })}</label>
            <input id="rh-date-to" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
          </div>

          <div className="rhField">
            <label htmlFor="rh-user-picker">{t('roomHandover.filters.userPicker', { defaultValue: 'Select user' })}</label>
            <select
              id="rh-user-picker"
              value={selectedUserId}
              onChange={(event) => setSelectedUserId(event.target.value)}
              disabled={loadingUsers}
            >
              <option value="">{t('roomHandover.filters.userPickerPlaceholder', { defaultValue: 'Choose user' })}</option>
              {users.map((item) => (
                <option key={item.id} value={item.id}>
                  #{item.id} - {item.firstName} {item.lastName}
                </option>
              ))}
            </select>
          </div>

          <div className="rhActions">
            <button
              type="button"
              className="rhBtn rhBtnWarn"
              onClick={handleCloseHotelDaily}
              disabled={closingDaily || loadingData || !shouldFilterByUser || (openFolios.length === 0 && handoverAmount === 0)}
            >
              {closingDaily
                ? t('roomHandover.buttons.closingDaily', { defaultValue: 'Closing...' })
                : t('roomHandover.buttons.closeHotelDaily', { defaultValue: 'Close Hotel Daily' })}
            </button>
            <button type="button" className="rhBtn rhBtnGhost" onClick={handleClear} disabled={loadingData}>
              {t('roomHandover.buttons.clear', { defaultValue: 'Clear' })}
            </button>
          </div>
        </div>

        {selectedUser ? (
          <div className="rhUserBanner">
            <strong>{t('roomHandover.selectedUser', { defaultValue: 'Selected user' })}:</strong> #{selectedUser.id} {selectedUser.firstName} {selectedUser.lastName}
          </div>
        ) : hasSearched ? (
          <div className="rhUserBanner">
            <strong>{t('roomHandover.selectedUser', { defaultValue: 'Selected user' })}:</strong>{' '}
            {t('roomHandover.allUsers', { defaultValue: 'All users' })}
          </div>
        ) : null}

        {error ? <div className="rhAlert rhAlertError">{error}</div> : null}

        <div className="rhStats">
          <div className="rhStat rhStatPrimary">
            <span>{t('roomHandover.stats.handoverAmount', { defaultValue: 'Amount' })}</span>
            <strong>{formatMoney(handoverAmount)}</strong>
            <small>
              {t('roomHandover.stats.handoverHint', {
                defaultValue: shouldFilterByUser
                  ? 'Payments not yet handed over for the selected user in the selected period.'
                  : 'All payments not yet handed over in the selected period.',
              })}
            </small>
          </div>

          <div className="rhStat">
            <span>{t('roomHandover.stats.totalPayments', { defaultValue: 'Payment records' })}</span>
            <strong>{paymentHistory.length}</strong>
            <small>{formatMoney(handoverAmount)}</small>
          </div>

          <div className="rhStat">
            <span>{t('roomHandover.stats.openFolios', { defaultValue: 'Open folios' })}</span>
            <strong>{openFolios.length}</strong>
            <small>{uniqueFolios.length} {t('roomHandover.stats.totalFolios', { defaultValue: 'folios' })}</small>
          </div>

          <div className="rhStat">
            <span>{t('roomHandover.stats.closedFolios', { defaultValue: 'Closed folios' })}</span>
            <strong>{closedFolios.length}</strong>
            <small>{uniqueFolios.length} {t('roomHandover.stats.totalFolios', { defaultValue: 'folios' })}</small>
          </div>
        </div>

        {!loadingData && hasSearched && paymentHistory.length === 0 && !error ? (
          <div className="rhAlert">{t('roomHandover.noResults', { defaultValue: 'No hotel folio payments were found for the selected filters.' })}</div>
        ) : null}

        <div className="rhPanels">
          <section className="rhPanel">
            <div className="rhPanelHead">
              <div>
                <h2>{t('roomHandover.sections.paymentHistoryTitle', { defaultValue: 'Payment history' })}</h2>
                <p>{t('roomHandover.sections.paymentHistorySubtitle', { defaultValue: 'Hotel folio payments for the selected dates and optional user filter.' })}</p>
              </div>
              <span>{paymentHistory.length}</span>
            </div>
            {renderPaymentHistory()}
          </section>
        </div>
      </div>
    </BasicCli>
  );
};

export default RoomHandoverPage;
